import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'https://silver-chicken-493994.hostingersite.com';

const MediatorDashboard = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState({
    total_listings: 0,
    active_listings: 0,
    pending_listings: 0,
    sold_listings: 0,
    total_views: 0,
    total_inquiries: 0,
  });
  const [recentProperties, setRecentProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/dashboard/mediator`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setStats(response.data.stats);
      setRecentProperties(response.data.recent_properties || []);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Mediator Dashboard</h1>
        <Link
          to="/mediator/new-property"
          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
        >
          <span className="text-xl">+</span> Post New Property
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-lg text-center">
          <p className="text-2xl font-bold text-blue-600">{stats.total_listings}</p>
          <p className="text-sm text-gray-600">Total Listings</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-lg text-center">
          <p className="text-2xl font-bold text-green-600">{stats.active_listings}</p>
          <p className="text-sm text-gray-600">Active</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-lg text-center">
          <p className="text-2xl font-bold text-yellow-600">{stats.pending_listings}</p>
          <p className="text-sm text-gray-600">Pending</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-lg text-center">
          <p className="text-2xl font-bold text-gray-600">{stats.sold_listings}</p>
          <p className="text-sm text-gray-600">Sold</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-lg text-center">
          <p className="text-2xl font-bold text-purple-600">{stats.total_views}</p>
          <p className="text-sm text-gray-600">Total Views</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-lg text-center">
          <p className="text-2xl font-bold text-orange-600">{stats.total_inquiries}</p>
          <p className="text-sm text-gray-600">Inquiries</p>
        </div>
      </div>

      {/* Recent Properties */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Your Recent Properties</h2>
        {recentProperties.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            No properties yet. 
            <Link to="/mediator/new-property" className="text-blue-600 hover:underline ml-1">
              Post your first property!
            </Link>
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Property ID</th>
                  <th className="text-left py-2">Title</th>
                  <th className="text-left py-2">Price</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-left py-2">Views</th>
                  <th className="text-left py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentProperties.map((prop) => (
                  <tr key={prop.id} className="border-b hover:bg-gray-50">
                    <td className="py-2 text-sm">{prop.property_id}</td>
                    <td className="py-2">{prop.title}</td>
                    <td className="py-2">₹{Number(prop.selling_price).toLocaleString()}</td>
                    <td className="py-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        prop.status === 'available' ? 'bg-green-100 text-green-800' :
                        prop.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {prop.status}
                      </span>
                    </td>
                    <td className="py-2">{prop.views_count || 0}</td>
                    <td className="py-2">
                      <Link 
                        to={`/property/${prop.property_id}`}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediatorDashboard;